import {
    AssistanceCoverage,
    PevnoAssistanceCoverage,
    UniqaAssistanceCoverage
} from '../../../../../core/consts/offer-modification-code.enum';
import {InsuranceCompanyCode} from '../../../../models/insurance-company-code.enum';

export class AssistanceCoveragesComparisonConfig {

    private icCode: InsuranceCompanyCode;
    private readonly translationsContainerKey = 'ASSISTANCE_COVERAGES_COMPARISON';

    constructor(icCode: InsuranceCompanyCode) {
        this.icCode = icCode;
    }

    createComparisonConfig(): AssistanceCoverageConfig[] {
        switch (this.icCode) {
            case InsuranceCompanyCode.UNIQA:
                return this.createUniqaConfig();
            case InsuranceCompanyCode.PEVNO:
                return this.createPevnoConfig();
            default:
                console.warn(`Unsupported ic code ${this.icCode} for generating assistance coverages comparison.`);
        }
    }

    getAssistanceCoveragesForComparison(): AssistanceCoverage[] {
        switch (this.icCode) {
            case InsuranceCompanyCode.UNIQA:
                return [
                    UniqaAssistanceCoverage.NONE,
                    UniqaAssistanceCoverage.STANDARD,
                    UniqaAssistanceCoverage.STANDARD_PLUS,
                    UniqaAssistanceCoverage.PREMIUM,
                    UniqaAssistanceCoverage.PRESTIGE,
                ];
            case InsuranceCompanyCode.PEVNO:
                return [
                    PevnoAssistanceCoverage.NONE,
                    PevnoAssistanceCoverage.ECONOMIC,
                    PevnoAssistanceCoverage.EXPANDED,
                    PevnoAssistanceCoverage.MAXIMUM,
                ];
            default:
                console.warn(`Unsupported ic code ${this.icCode} for generating assistance coverages order.`);
        }
    }

    private createUniqaConfig(): AssistanceCoverageConfig[] {
        return [
            {
                coverageName: AssistanceCoverageName.PACKAGE_NAME,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.midi`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.standard`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.standardPlus`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.premium`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.prestige`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.TERRITORIAL_RANGE,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.polandAndEurope`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.polandAndEurope`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_HAPPENING,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_DISTANCE_HELP,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_ACCIDENT_PL,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.75km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.200km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.withoutLimits`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.400km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.withoutLimits`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_BREAKDOWN_PL,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.200km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.withoutLimits`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.400km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.withoutLimits`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_EU,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.400km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.1600km`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR,
                label: 'ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR_UNIQA',
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.2days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.15days`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_BREAKDOWN_REPLACEMENT_CAR,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.2days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.4days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.4days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.4days`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPAIR_CAR,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPLACEMENT_OF_WHEELS,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_BATTERY,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_FUEL,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_KEYS,
                label: 'ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.ASSISTANCE_KEYS_UNIQA',
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.DRIVER_AND_PASSENGERS_TRANSPORT,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.FUEL_CONDITION_HELP,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_VEHICLE_SCRAPPING,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPAIR_PARTS,
                values: [
                    {
                        coverage: UniqaAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.STANDARD_PLUS,
                        value: false
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PREMIUM,
                        value: true
                    },
                    {
                        coverage: UniqaAssistanceCoverage.PRESTIGE,
                        value: true
                    }
                ]
            }
        ];
    }

    private createPevnoConfig(): AssistanceCoverageConfig[] {
        return [
            {
                coverageName: AssistanceCoverageName.PACKAGE_NAME,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.basicPevno`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.economic`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.expanded`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.PACKAGE_NAME.maximum`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.TERRITORIAL_RANGE,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.poland`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.TERRITORIAL_RANGE.polandAndEurope`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_HAPPENING,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accident`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_HAPPENING.accidentBreakdownTheft`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_DISTANCE_HELP,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_DISTANCE_HELP.none`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_ACCIDENT_PL,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.75km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.100km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.300km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.500km`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_BREAKDOWN_PL,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.100km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.300km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.500km`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_TOWING_EU,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_TOWING.500km`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR,
                label: 'ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR_UNIQA',
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.2days`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.4days`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_BREAKDOWN_REPLACEMENT_CAR,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.2days`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.4days`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.ASSISTANCE_REPLACEMENT_CAR.7days`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPAIR_CAR,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPLACEMENT_OF_WHEELS,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_BATTERY,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_FUEL,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_KEYS,
                label: 'ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.ASSISTANCE_KEYS_UNIQA',
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.DRIVER_AND_PASSENGERS_TRANSPORT,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: `${this.translationsContainerKey}.DRIVER_AND_PASSENGERS_TRANSPORT.above25km`
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.FUEL_CONDITION_HELP,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: true
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_VEHICLE_SCRAPPING,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            },
            {
                coverageName: AssistanceCoverageName.ASSISTANCE_REPAIR_PARTS,
                values: [
                    {
                        coverage: PevnoAssistanceCoverage.NONE,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.ECONOMIC,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.EXPANDED,
                        value: false
                    },
                    {
                        coverage: PevnoAssistanceCoverage.MAXIMUM,
                        value: true
                    }
                ]
            }
        ]
    }

}

export interface AssistanceCoverageConfig {
    coverageName: AssistanceCoverageName;
    label?: string;
    values: AssistanceCoverageValue[];
}

export interface AssistanceCoverageValue {
    coverage: AssistanceCoverage;
    value: string | boolean;
}

export interface AssistanceCoverageVariantPricing {
    name: AssistanceCoverage;
    price?: number;
}

export enum AssistanceCoverageName {
    PACKAGE_NAME = 'PACKAGE_NAME',
    TERRITORIAL_RANGE = 'TERRITORIAL_RANGE',
    ASSISTANCE_HAPPENING = 'ASSISTANCE_HAPPENING',
    ASSISTANCE_DISTANCE_HELP = 'ASSISTANCE_DISTANCE_HELP',
    ASSISTANCE_TOWING_ACCIDENT_PL = 'ASSISTANCE_TOWING_ACCIDENT_PL',
    ASSISTANCE_TOWING_BREAKDOWN_PL = 'ASSISTANCE_TOWING_BREAKDOWN_PL',
    ASSISTANCE_TOWING_EU = 'ASSISTANCE_TOWING_EU',
    ASSISTANCE_ACCIDENT_REPLACEMENT_CAR = 'ASSISTANCE_ACCIDENT_REPLACEMENT_CAR',
    ASSISTANCE_BREAKDOWN_REPLACEMENT_CAR = 'ASSISTANCE_BREAKDOWN_REPLACEMENT_CAR',
    ASSISTANCE_REPAIR_CAR = 'ASSISTANCE_REPAIR_CAR',
    ASSISTANCE_REPLACEMENT_OF_WHEELS = 'ASSISTANCE_REPLACEMENT_OF_WHEELS',
    ASSISTANCE_BATTERY = 'ASSISTANCE_BATTERY',
    ASSISTANCE_FUEL = 'ASSISTANCE_FUEL',
    ASSISTANCE_KEYS = 'ASSISTANCE_KEYS',
    ASSISTANCE_REPAIR_PARTS = 'ASSISTANCE_REPAIR_PARTS',
    ASSISTANCE_ACCIDENT_HOSPITALIZATION = 'ASSISTANCE_ACCIDENT_HOSPITALIZATION',
    ASSISTANCE_LEGAL_PROTECTION_ABROAD = 'ASSISTANCE_LEGAL_PROTECTION_ABROAD',
    ASSISTANCE_VEHICLE_SCRAPPING = 'ASSISTANCE_VEHICLE_SCRAPPING',
    DRIVER_AND_PASSENGERS_TRANSPORT = 'DRIVER_AND_PASSENGERS_TRANSPORT',
    FUEL_CONDITION_HELP = 'FUEL_CONDITION_HELP',
}
